import React from 'react';
import { isMobile } from 'react-device-detect';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Box, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';

import { ErrorBoundaryFallback } from '@/components/ErrorBoundaryFallback';
import LoadingScreen from '@/components/LoadingScreen';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

import { useMobileResizeHeight } from './mobile/useMobileResizeHeight';
import { useMobileViewportFacilitySelection } from './mobile/useMobileViewportFacilitySelection';

import { CrmRequireSelectedFacility } from './CrmRequireSelectedFacility';
import Sidebar from './Sidebar';

const Layout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  window.getAccessTokenSilently = getAccessTokenSilently;
  const { isError, isLoading, error } = useCurrentUser();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMobileViewport = useIsMobileViewport();
  useMobileResizeHeight(containerRef);
  useMobileViewportFacilitySelection();

  if (isLoading) {
    return <LoadingScreen animate />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <Sentry.ErrorBoundary
        showDialog
        fallback={({ error, resetError }) => (
          <ErrorBoundaryFallback error={error} resetError={resetError} />
        )}
      />
    );
  }

  return isMobileViewport ? (
    <>{children}</>
  ) : (
    <Stack
      ref={containerRef}
      sx={{
        height: isMobile ? window.innerHeight : '100vh'
      }}
      direction="row"
    >
      <Sidebar />
      <Stack
        component="main"
        className="main-content"
        direction="column"
        sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
      >
        <Box
          id="scrollable-content"
          sx={{
            margin: 2,
            marginLeft: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1
          }}
        >
          <CrmRequireSelectedFacility>{children}</CrmRequireSelectedFacility>
        </Box>
      </Stack>
    </Stack>
  );
};

const LayoutWithAuthenticationRequired = (props: React.PropsWithChildren) => {
  // Set in CurrentUserPopover component before logging out
  const login_hint = localStorage.getItem('login_hint') ?? undefined;

  React.useEffect(() => {
    return () => {
      localStorage.removeItem('login_hint');
    };
  }, []);

  const Rendered = withAuthenticationRequired(Layout, {
    onRedirecting: () => <LoadingScreen />,
    loginOptions: {
      login_hint
    }
  });
  return <Rendered {...props} />;
};

export default LayoutWithAuthenticationRequired;
